<template>
  <div :class="{ 'is-invalid': showValidationError }">
    <toast v-if="message" :kind="message.status">
      <template #message-content>
        {{ message.message }}
      </template>
    </toast>

    <label v-if="label && showLabel" class="form-label" :for="id">
      <required v-if="required" />
      <span v-html="label" />
    </label>

    <select :id="id" ref="select" v-model="value" :name="field"
            :class="['form-select', inputClass, { 'is-invalid': showValidationError }]"
            @change="setSelection">
      <option v-if="showBlank" disabled selected value />

      <option v-for="option in options" :key="valueForOption(option)" :value="valueForOption(option)"
              v-html="displayNameForOption(option)" />
    </select>

    <p v-if="helpText" class="text-muted" v-html="helpText" />

    <validation-errors v-if="showValidationError" :errors="validationErrors" />
  </div>
</template>

<script>
import Required from '../required'
import Toast from '../toast'
import ValidationErrors from './validation-errors'
import PersistAndToast from './mixins/persist-and-toast'

export default {
  components: { Required, Toast, ValidationErrors },
  mixins: [PersistAndToast],

  props: {
    helpText: {
      type: String,
      required: false
    },

    label: {
      type: String,
      required: false
    },

    id: {
      type: String,
      required: false
    },

    inputClass: {
      type: String,
      required: false,
      default: ''
    },

    field: {
      type: String,
      required: true
    },

    options: {
      type: Array,
      required: true
    },

    initialValue: {
      type: [Number, String],
      required: false
    },

    required: {
      type: Boolean,
      required: false,
      default: false
    },

    showLabel: {
      type: Boolean,
      required: false,
      default: true
    },

    validationErrors: {
      type: Array,
      required: false
    },

    blankIfNoSelection: {
      type: Boolean,
      required: false,
      default: false
    }
  },

  emits: ['changed'],

  data() {
    return {
      value: this.initialValue,
      rubberBandOnError: false
    }
  },

  computed: {
    showValidationError() {
      return this.validationErrors && this.validationErrors.length > 0
    },

    showBlank() {
      return this.value === null && this.blankIfNoSelection
    }
  },

  watch: {
    options: {
      handler() {
        // When the `options` prop changes, it's possible that the selected `value` is no longer a valid option.
        // If that happens, the browser is likely to display the dropdown as if the first item is selected.
        // To avoid bugs where what the user sees does not match the value that may be saved when sending data to
        // the server, we explicitly set the value to the first option in this case.
        if (this.optionWithValue(this.value) === undefined) {
          this.value = this.valueForOption(this.options[0])
          this.$emit('changed', this.valueForOption(this.options[0]))
        }
      },
      deep: true
    }
  },

  methods: {
    displayNameForOption(option) {
      return option[0]
    },

    focus() {
      this.$refs.select.focus()
    },

    optionWithValue(value) {
      return this.options.find((option) => {
        if (this.valueForOption(option) === null) {
          return value === null
        } else if (value === null) {
          return false
        } else {
          return this.valueForOption(option)?.toString() === value?.toString()
        }
      })
    },

    setSelection(e) {
      let selectedOption = this.optionWithValue(e.target.value)
      if (!selectedOption) {
        // If the user selects the option with null value, e.target.value will not be null,
        // it will be the display text of that option (because of a quirk of the HTML model).
        // See if that's what happened.
        const optionWithNullValue = this.optionWithValue(null)
        if (this.displayNameForOption(optionWithNullValue) === e.target.value) {
          selectedOption = optionWithNullValue
        }
      }

      this.$emit('changed', this.valueForOption(selectedOption))

      if (this.persist === true) {
        this.save(e.target)
      }
    },

    valueForOption(option) {
      return option[1]
    },

    setValue(newValue) {
      this.value = newValue
    }
  }
}
</script>
